import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Eye, Bus, MapPin, Calendar, X,
  ChevronLeft, ChevronRight, FileText, ClipboardList, Download,
  Printer, Package, Truck, MessageCircle, Ticket, Phone, CreditCard, Navigation, Receipt,
  Clock, UserCheck, Image, ExternalLink,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useLoading } from '../contexts/LoadingContext';
import { useTenant } from '../contexts/TenantContext';
import { moduleEnabled } from '../config/modules';
import ExportFormatModal from '../components/ExportFormatModal';
import {
  exportMotoristaXLSX, exportBagagemXLSX, exportEncomendasXLSX,
  exportEmbarqueXLSX, exportRelatorioXLSX,
} from '../utils/excelExport';

const STATUS_OPTIONS = [
  { value: 'agendada', label: 'Agendada', cls: 'bg-blue-100 text-blue-700' },
  { value: 'em_andamento', label: 'Em Andamento', cls: 'bg-amber-100 text-amber-700' },
  { value: 'finalizada', label: 'Finalizada', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'cancelada', label: 'Cancelada', cls: 'bg-red-100 text-red-700' },
];
const getStatusCls = s => STATUS_OPTIONS.find(o => o.value === s)?.cls || 'bg-slate-100 text-slate-700';
const getStatusLabel = s => STATUS_OPTIONS.find(o => o.value === s)?.label || s;

const DIFICULDADE_OPTIONS = [
  { value: 'Leve',      cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'Moderado',  cls: 'bg-amber-100 text-amber-700' },
  { value: 'Pesado',    cls: 'bg-orange-100 text-orange-700' },
  { value: 'Expedição', cls: 'bg-red-100 text-red-700' },
];
const getDificuldadeCls = v => DIFICULDADE_OPTIONS.find(o => o.value === v)?.cls || 'bg-slate-100 text-slate-700';
const ATIVIDADE_OPTIONS = ['Trekking', 'Montanha', 'Litoral / Praia', 'Cultural', 'Aventura radical', 'Rural / Ecoturismo', 'City Tour'];

const EMPTY = { titulo: '', origem: '', destino: '', data: '', horario: '', veiculo: '', motorista: '', capacidade: 44, observacoes: '', status: 'agendada', dificuldade: '', tipoAtividade: '', guia_id: '', itinerario: [], galeriaUrl: '' };

function driveEmbedUrl(url) {
  if (!url) return '';
  const m = url.match(/folders\/([a-zA-Z0-9_-]+)/);
  if (m) return `https://drive.google.com/embeddedfolderview?id=${m[1]}#grid`;
  const idp = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idp) return `https://drive.google.com/embeddedfolderview?id=${idp[1]}#grid`;
  return url;
}

function getReservaStatusStyle(status, formaPag) {
  if (formaPag === 'Cortesia') return { cls: 'bg-blue-100 text-blue-700', label: 'Cortesia' };
  if (formaPag === 'A Pagar')  return { cls: 'bg-orange-100 text-orange-700', label: 'A Pagar' };
  if (status === 'confirmada') return { cls: 'bg-emerald-100 text-emerald-700', label: 'Confirmada' };
  if (status === 'pendente')   return { cls: 'bg-amber-100 text-amber-700', label: 'Pendente' };
  if (status === 'cancelada')  return { cls: 'bg-red-100 text-red-700', label: 'Cancelada' };
  return { cls: 'bg-slate-100 text-slate-600', label: status };
}

function waLink(tel) {
  const d = (tel || '').replace(/\D/g, '');
  return d ? `https://wa.me/55${d}` : null;
}

function SeatPopup({ seat, reserva, onClose, onPrintSeat }) {
  const { cls, label } = getReservaStatusStyle(reserva.status, reserva.forma_pagamento);
  const wa = waLink(reserva.cliente?.telefone);
  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="fixed z-[61] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] bg-white rounded-2xl shadow-2xl border border-slate-200"
        onClick={e => e.stopPropagation()}>
        <div className="rounded-t-2xl px-4 py-3 flex items-center justify-between"
          style={{ background: 'linear-gradient(to right, var(--brand-primary, #0147a0), #0369a1)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <span className="text-white font-bold text-sm">{seat}</span>
            </div>
            <div>
              <p className="text-white/70 text-[10px] uppercase tracking-wider font-medium">Poltrona</p>
              <p className="text-white font-semibold text-sm leading-tight">{reserva.cliente?.nome || '—'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-white/60 hover:text-white hover:bg-white/20"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 py-3 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>{label}</span>
            {reserva.valor > 0 && <span className="text-slate-700 font-bold text-sm">R$ {Number(reserva.valor).toFixed(2)}</span>}
          </div>
          <div className="space-y-2 text-xs">
            {reserva.cliente?.telefone && <div className="flex items-center gap-2 text-slate-600"><Phone className="w-3.5 h-3.5 text-slate-400" /><span>{reserva.cliente.telefone}</span></div>}
            {reserva.forma_pagamento && !['Cortesia', 'A Pagar'].includes(reserva.forma_pagamento) && <div className="flex items-center gap-2 text-slate-600"><CreditCard className="w-3.5 h-3.5 text-slate-400" /><span>{reserva.forma_pagamento}</span></div>}
            {reserva.local_embarque && <div className="flex items-center gap-2 text-slate-600"><Navigation className="w-3.5 h-3.5 text-slate-400" /><span>Embarque: <strong>{reserva.local_embarque}</strong></span></div>}
          </div>
        </div>
        <div className="px-4 pb-4 flex gap-2">
          {wa && <a href={wa} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-xl"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>}
          {onPrintSeat && <button onClick={() => { onPrintSeat(seat); onClose(); }} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-white text-xs font-semibold rounded-xl" style={{ background: 'var(--brand-primary, #0147a0)' }}><Ticket className="w-3.5 h-3.5" /> Passagem</button>}
        </div>
      </div>
    </>
  );
}

function MiniSeatMap({ capacidade, reservas, onPrintSeat }) {
  const [selectedSeat, setSelectedSeat] = useState(null);
  const reservaMap = {};
  (reservas || []).filter(r => r.status !== 'cancelada').forEach(r => { reservaMap[r.assento] = r; });
  const ocupados = new Set(Object.keys(reservaMap).map(Number));
  const numRows = Math.ceil(capacidade / 4);
  const rows = [];
  for (let i = 0; i < numRows; i++) { const b = i * 4; rows.push([b+1, b+2, null, b+4, b+3]); }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-1.5"><Bus className="w-4 h-4 text-slate-400" />Mapa de Poltronas</h3>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{capacidade - ocupados.size} livres</span>
          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">{ocupados.size} ocupados</span>
        </div>
      </div>
      <div className="rounded-xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="bg-gradient-to-b from-slate-700 to-slate-600 px-3 pt-2 pb-2">
          <div className="h-7 bg-slate-500/30 rounded border border-slate-500/40 flex items-center justify-center">
            <span className="text-slate-400 text-[8px] font-medium uppercase tracking-widest">Para-brisa / Motorista</span>
          </div>
        </div>
        <div className="bg-slate-100 border-b border-slate-200 px-3 py-1"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 14px 1fr 1fr', gap: '4px' }}>
          {['JAN.', 'COR.', '', 'COR.', 'JAN.'].map((h, i) => (
            <div key={i} className="text-center text-[8px] font-bold text-slate-400 uppercase tracking-wider">{h}</div>
          ))}
        </div>
        <div className="px-3 py-2 space-y-1 max-h-56 overflow-y-auto">
          {rows.map((row, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 14px 1fr 1fr', gap: '4px', alignItems: 'center' }}>
              {row.map((num, j) => {
                if (num === null) return <div key={j} className="flex items-center justify-center h-7"><div className="w-px h-full" style={{ background: 'repeating-linear-gradient(to bottom,#cbd5e1 0,#cbd5e1 3px,transparent 3px,transparent 6px)' }} /></div>;
                if (num > capacidade) return <div key={j} className="h-7" />;
                const isOcc = ocupados.has(num);
                const isSel = selectedSeat === num;
                return (
                  <button key={j} type="button" onClick={() => isOcc ? setSelectedSeat(isSel ? null : num) : null}
                    className={`h-7 rounded-md text-[10px] font-bold flex items-center justify-center transition-all ${isOcc ? `bg-red-100 text-red-500 border border-red-200 cursor-pointer hover:bg-red-200 ${isSel ? 'ring-2 ring-red-400 ring-offset-1 bg-red-200' : ''}` : 'bg-emerald-100 text-emerald-700 border border-emerald-300 cursor-default'}`}>
                    {num}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="bg-slate-100 border-t border-slate-200 py-1 text-center">
          <span className="text-[7px] font-medium text-slate-400 uppercase tracking-widest">— Fundo do Ônibus —</span>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded inline-block"/>Livre</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-100 border border-red-200 rounded inline-block"/>Ocupado (clique para ver)</span>
      </div>
      {selectedSeat && reservaMap[selectedSeat] && (
        <SeatPopup seat={selectedSeat} reserva={reservaMap[selectedSeat]} onClose={() => setSelectedSeat(null)} onPrintSeat={onPrintSeat} />
      )}
    </div>
  );
}

function ItinerarioEditor({ value, onChange, inp }) {
  const dias = Array.isArray(value) ? value : [];
  const update = (i, k, v) => onChange(dias.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const addDia = () => onChange([...dias, { titulo: '', hospedagem: '', refeicoes: '', atividades: '' }]);
  const removeDia = (i) => onChange(dias.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold text-slate-700">Itinerário (dia a dia)</label>
        <button type="button" onClick={addDia} className="text-xs flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
          <Plus className="w-3 h-3" /> Adicionar dia
        </button>
      </div>
      {dias.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Nenhum dia adicionado.</p>
      ) : (
        <div className="space-y-3">
          {dias.map((d, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Dia {i + 1}</span>
                <button type="button" onClick={() => removeDia(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <input value={d.titulo || ''} onChange={e => update(i, 'titulo', e.target.value)} placeholder="Título do dia (ex: Chegada e trilha leve)" className={inp} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={d.hospedagem || ''} onChange={e => update(i, 'hospedagem', e.target.value)} placeholder="Hospedagem" className={inp} />
                <input value={d.refeicoes || ''} onChange={e => update(i, 'refeicoes', e.target.value)} placeholder="Refeições (café, almoço, jantar)" className={inp} />
              </div>
              <textarea value={d.atividades || ''} onChange={e => update(i, 'atividades', e.target.value)} rows={2} placeholder="Atividades / atrações do dia" className={inp + ' resize-none'} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WaitlistSection({ viagemId, canWrite }) {
  const [entries, setEntries] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [clienteId, setClienteId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/viagens/${viagemId}/waitlist`);
      setEntries(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [viagemId]);

  useEffect(() => { load(); }, [load]);

  const loadClientes = async () => {
    if (clientes.length) return;
    try { const { data } = await api.get('/clientes'); setClientes(data); } catch { /* noop */ }
  };

  const add = async () => {
    if (!clienteId) return;
    try {
      await api.post(`/viagens/${viagemId}/waitlist`, { cliente_id: clienteId });
      toast.success('Adicionado à lista de espera');
      setClienteId(''); setAdding(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao adicionar'); }
  };

  const promote = async (id) => {
    try {
      await api.post(`/waitlist/${id}/promover`, {});
      toast.success('Promovido para reserva! Ajuste os detalhes em Reservas.');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao promover'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/waitlist/${id}`); load(); } catch { toast.error('Erro ao remover'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-slate-400" /> Lista de espera{entries.length > 0 ? ` (${entries.length})` : ''}
        </h3>
        {canWrite && !adding && (
          <button type="button" onClick={() => { setAdding(true); loadClientes(); }} className="text-xs flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
            <Plus className="w-3 h-3" /> Adicionar
          </button>
        )}
      </div>
      {adding && (
        <div className="flex gap-2 mb-3">
          <select value={clienteId} onChange={e => setClienteId(e.target.value)} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Selecione o cliente...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <button type="button" onClick={add} className="px-3 py-2 text-white rounded-lg text-sm font-medium" style={{ background: 'var(--brand-primary, #0147a0)' }}>Adicionar</button>
          <button type="button" onClick={() => { setAdding(false); setClienteId(''); }} className="px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600">Cancelar</button>
        </div>
      )}
      {loading ? (
        <p className="text-xs text-slate-400">Carregando...</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Ninguém na lista de espera.</p>
      ) : (
        <div className="space-y-1.5">
          {entries.map((e, i) => (
            <div key={e.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{e.cliente?.nome || '—'}</p>
                  {e.cliente?.telefone && <p className="text-xs text-slate-400">{e.cliente.telefone}</p>}
                </div>
              </div>
              {canWrite && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button type="button" onClick={() => promote(e.id)} title="Promover para reserva" className="text-xs px-2 py-1 rounded-lg text-emerald-600 hover:bg-emerald-50 font-medium flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> Promover</button>
                  <button type="button" onClick={() => remove(e.id)} title="Remover" className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Viagens() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const { branding } = useTenant();
  const showDificuldade = moduleEnabled(branding, 'dificuldade');
  const showAtividade = moduleEnabled(branding, 'atividade');
  const showGuias = moduleEnabled(branding, 'guias');
  const showItinerario = moduleEnabled(branding, 'itinerario');
  const showGaleria = moduleEnabled(branding, 'galeria');
  const showWaitlist = moduleEnabled(branding, 'waitlist');
  const [guias, setGuias] = useState([]);
  const canWrite = user?.role !== 'financeiro';
  const [viagens, setViagens] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [exportModal, setExportModal] = useState({ open: false, viagemId: null, type: null, label: '' });
  const PER_PAGE = 10;

  const REPORT_LABELS = { motorista: 'Lista para Motorista', bagagem: 'Lista de Bagagem', encomendas: 'Lista de Encomendas', embarque: 'Bilhetes de Embarque', relatorio: 'Relatório Completo' };
  const openExportModal = (viagemId, type) => setExportModal({ open: true, viagemId, type, label: REPORT_LABELS[type] || 'Relatório' });
  const closeExportModal = () => setExportModal({ open: false, viagemId: null, type: null, label: '' });

  const fetchViagens = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/viagens', { params: { search: search || undefined, status: statusFilter || undefined, data_inicio: dataInicio || undefined, data_fim: dataFim || undefined } });
      setViagens(data);
      setPage(1);
    } catch { toast.error('Erro ao carregar viagens'); }
    finally { setLoading(false); }
  }, [search, statusFilter, dataInicio, dataFim]);

  useEffect(() => { fetchViagens(); }, [statusFilter, dataInicio, dataFim]);

  useEffect(() => {
    if (!showGuias) return;
    api.get('/guias').then(({ data }) => setGuias(data)).catch(() => {});
  }, [showGuias]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = v => { setEditingId(v.id); setForm({ ...v }); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(EMPTY); };

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, capacidade: parseInt(form.capacidade) };
      if (editingId) { await api.put(`/viagens/${editingId}`, payload); toast.success('Viagem atualizada!'); }
      else { await api.post('/viagens', payload); toast.success('Viagem criada!'); }
      closeModal(); fetchViagens();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/viagens/${deleteId}`); toast.success('Viagem excluída!'); setDeleteId(null); fetchViagens(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get('/viagens/export/csv', { params: { search: search || undefined, status: statusFilter || undefined, data_inicio: dataInicio || undefined, data_fim: dataFim || undefined }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a'); a.href = url; a.setAttribute('download', 'viagens.csv');
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('CSV exportado!');
    } catch { toast.error('Erro ao exportar CSV'); }
    finally { setExporting(false); }
  };

  const downloadRecibo = async (reservaId, nomeCliente) => {
    setLoadingPDF(true); showLoading();
    try {
      const res = await api.get(`/reservas/${reservaId}/recibo-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      const nome = (nomeCliente || reservaId).slice(0, 25).replace(/\s+/g, '-');
      a.setAttribute('download', `recibo-${nome}.pdf`);
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Recibo gerado!');
    } catch { toast.error('Erro ao gerar recibo'); }
    finally { setLoadingPDF(false); hideLoading(); }
  };

  const downloadPDF = async (viagemId, type, assento = null) => {
    const epMap = { relatorio: 'relatorio-pdf', embarque: 'lista-embarque/pdf', motorista: 'lista-motorista/pdf', bagagem: 'lista-bagagem/pdf', encomendas: 'lista-encomendas/pdf' };
    const ep = epMap[type] || 'relatorio-pdf';
    setLoadingPDF(true); showLoading();
    try {
      const params = assento ? { assentos: assento } : {};
      const res = await api.get(`/viagens/${viagemId}/${ep}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.setAttribute('download', assento ? `passagem-${assento}-${viagemId.slice(0,8)}.pdf` : `${type}-${viagemId.slice(0,8)}.pdf`);
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(assento ? `Passagem poltrona ${assento} gerada!` : `${REPORT_LABELS[type] || 'PDF'} gerado!`);
    } catch { toast.error('Erro ao gerar PDF'); }
    finally { setLoadingPDF(false); hideLoading(); }
  };

  const downloadExcel = async (viagemId, type) => {
    setLoadingExcel(true);
    try {
      const { data: viagem } = await api.get(`/viagens/${viagemId}`);
      if (type === 'motorista') exportMotoristaXLSX(viagem);
      else if (type === 'embarque') exportEmbarqueXLSX(viagem);
      else if (type === 'relatorio') exportRelatorioXLSX(viagem);
      else if (type === 'bagagem') { const { data: bags } = await api.get('/bagagens', { params: { viagem_id: viagemId } }); exportBagagemXLSX(viagem, bags); }
      else if (type === 'encomendas') { const { data: encs } = await api.get('/encomendas', { params: { viagem_id: viagemId } }); exportEncomendasXLSX(viagem, encs); }
      toast.success(`${REPORT_LABELS[type] || 'Relatório'} exportado!`);
    } catch { toast.error('Erro ao gerar Excel'); }
    finally { setLoadingExcel(false); }
  };

  const handleViewDetail = async id => {
    try { const { data } = await api.get(`/viagens/${id}`); setViewModal(data); }
    catch { toast.error('Erro ao carregar detalhes'); }
  };

  const paginated = viagens.slice((page-1)*PER_PAGE, page*PER_PAGE);
  const totalPages = Math.ceil(viagens.length / PER_PAGE);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]/20";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Viagens</h1>
          <p className="text-slate-500 text-sm mt-0.5">{viagens.length} viagem(ns) encontrada(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportCSV} disabled={exporting}
            className="flex items-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 font-medium px-4 py-2.5 rounded-lg text-sm disabled:opacity-60 transition-colors">
            <Download className="w-4 h-4" />{exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
          {canWrite && (
            <button onClick={openCreate}
              className="flex items-center gap-2 text-white font-medium px-4 py-2.5 rounded-lg text-sm transition-colors"
              style={{ background: 'var(--brand-secondary, #d72d21)' }}>
              <Plus className="w-4 h-4" /> Nova Viagem
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchViagens()}
              placeholder="Buscar por título, origem ou destino..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-[var(--brand-primary)]" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none">
            <option value="">Todos os status</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex items-center gap-2 flex-1">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-500 mb-1">Data início</label>
              <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-[var(--brand-primary)]" />
            </div>
            <span className="text-slate-400 text-sm mt-4">até</span>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-500 mb-1">Data fim</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-[var(--brand-primary)]" />
            </div>
          </div>
          <div className="flex gap-2">
            {(dataInicio || dataFim) && (
              <button onClick={() => { setDataInicio(''); setDataFim(''); }} className="px-3 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50">
                Limpar datas
              </button>
            )}
            <button onClick={fetchViagens} className="px-4 py-2 text-white rounded-lg text-sm font-medium" style={{ background: 'var(--brand-primary, #0147a0)' }}>
              Buscar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando...</div>
        ) : viagens.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Bus className="w-12 h-12 mb-3 opacity-20" />
            <p className="font-medium">Nenhuma viagem encontrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Viagem</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Data</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Veículo</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Assentos</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginated.map(v => (
                    <tr key={v.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-slate-800">{v.titulo}</p>
                        <div className="flex items-center gap-1 mt-0.5 text-slate-400 text-xs"><MapPin className="w-3 h-3" /><span>{v.origem} → {v.destino}</span></div>
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <div className="flex items-center gap-1 text-slate-600 text-xs"><Calendar className="w-3 h-3 text-slate-400" />{new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR')} {v.horario}</div>
                      </td>
                      <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell text-xs">{v.veiculo}</td>
                      <td className="px-5 py-3.5">
                        <div className="text-xs">
                          <span className="font-medium text-emerald-600">{v.assentos_livres} livres</span>
                          <span className="text-slate-400"> / </span>
                          <span className="font-medium text-red-500">{v.assentos_ocupados} ocupados</span>
                        </div>
                        <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden mt-1">
                          <div className="h-full bg-orange-500 rounded-full" style={{ width: `${v.capacidade > 0 ? (v.assentos_ocupados / v.capacidade) * 100 : 0}%` }} />
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusCls(v.status)}`}>{getStatusLabel(v.status)}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-0.5">
                          <button onClick={() => handleViewDetail(v.id)} title="Ver detalhes" className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openExportModal(v.id, 'motorista')} title="Lista para Motorista" className="p-1.5 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"><Printer className="w-4 h-4" /></button>
                          {moduleEnabled(branding, 'bagagem') && (
                            <button onClick={() => openExportModal(v.id, 'bagagem')} title="Lista de Bagagem" className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"><Package className="w-4 h-4" /></button>
                          )}
                          {moduleEnabled(branding, 'encomendas') && (
                            <button onClick={() => openExportModal(v.id, 'encomendas')} title="Lista de Encomendas" className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"><Truck className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => openExportModal(v.id, 'relatorio')} title="Relatório de Passageiros" className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><FileText className="w-4 h-4" /></button>
                          <button onClick={() => openExportModal(v.id, 'embarque')} title="Lista de Embarque" className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"><ClipboardList className="w-4 h-4" /></button>
                          {canWrite && (
                            <>
                              <button onClick={() => openEdit(v)} title="Editar" className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><Edit2 className="w-4 h-4" /></button>
                              <button onClick={() => setDeleteId(v.id)} title="Excluir" className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-500">Mostrando {(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, viagens.length)} de {viagens.length}</span>
                <div className="flex gap-2">
                  <button disabled={page === 1} onClick={() => setPage(p => p-1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
                  <button disabled={page === totalPages} onClick={() => setPage(p => p+1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal criar/editar */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg">{editingId ? 'Editar Viagem' : 'Nova Viagem'}</h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Título da Viagem *</label>
                <input value={form.titulo} onChange={e => f('titulo', e.target.value)} required autoFocus className={inp} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Origem *</label><input value={form.origem} onChange={e => f('origem', e.target.value)} required className={inp} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Destino *</label><input value={form.destino} onChange={e => f('destino', e.target.value)} required className={inp} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Data *</label><input type="date" value={form.data} onChange={e => f('data', e.target.value)} required className={inp} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Horário *</label><input type="time" value={form.horario} onChange={e => f('horario', e.target.value)} required className={inp} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Veículo *</label><input value={form.veiculo} onChange={e => f('veiculo', e.target.value)} required placeholder="Ex: Ônibus 44px" className={inp} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Motorista</label><input value={form.motorista || ''} onChange={e => f('motorista', e.target.value)} className={inp} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Capacidade *</label><input type="number" min="1" max="100" value={form.capacidade} onChange={e => f('capacidade', e.target.value)} required className={inp} /></div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select value={form.status} onChange={e => f('status', e.target.value)} className={inp + ' bg-white'}>
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                {showDificuldade && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Nível de dificuldade</label>
                    <select value={form.dificuldade || ''} onChange={e => f('dificuldade', e.target.value)} className={inp + ' bg-white'}>
                      <option value="">— Não definido</option>
                      {DIFICULDADE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value}</option>)}
                    </select>
                  </div>
                )}
                {showAtividade && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Tipo de atividade</label>
                    <select value={form.tipoAtividade || ''} onChange={e => f('tipoAtividade', e.target.value)} className={inp + ' bg-white'}>
                      <option value="">— Não definido</option>
                      {ATIVIDADE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
                {showGuias && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Guia responsável</label>
                    <select value={form.guia_id || ''} onChange={e => f('guia_id', e.target.value)} className={inp + ' bg-white'}>
                      <option value="">— Sem guia</option>
                      {guias.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label><textarea value={form.observacoes || ''} onChange={e => f('observacoes', e.target.value)} rows={2} className={inp + ' resize-none'} /></div>
              {showItinerario && (
                <ItinerarioEditor value={form.itinerario || []} onChange={v => f('itinerario', v)} inp={inp} />
              )}
              {showGaleria && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Galeria — link da pasta no Google Drive</label>
                  <input value={form.galeriaUrl || ''} onChange={e => f('galeriaUrl', e.target.value)} placeholder="https://drive.google.com/drive/folders/..." className={inp} />
                  <p className="text-xs text-slate-400 mt-1">Compartilhe a pasta como "qualquer pessoa com o link pode ver" para as fotos aparecerem aqui dentro.</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60" style={{ background: 'var(--brand-secondary, #d72d21)' }}>
                  {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar Viagem'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal detalhe */}
      {viewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg">{viewModal.titulo}</h2>
              <button onClick={() => setViewModal(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Rota</p><p className="font-semibold text-slate-800 text-sm">{viewModal.origem} → {viewModal.destino}</p></div>
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Data e Hora</p><p className="font-semibold text-slate-800 text-sm">{new Date(viewModal.data + 'T00:00:00').toLocaleDateString('pt-BR')} às {viewModal.horario}</p></div>
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Assentos</p><p className="text-sm"><span className="font-semibold text-emerald-600">{viewModal.assentos_livres} livres</span><span className="text-slate-400 mx-1">/</span><span className="font-semibold text-red-500">{viewModal.assentos_ocupados} ocupados</span><span className="text-slate-400 mx-1">/</span><span className="text-slate-600">{viewModal.capacidade} total</span></p></div>
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Status</p><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusCls(viewModal.status)}`}>{getStatusLabel(viewModal.status)}</span></div>
                {showDificuldade && viewModal.dificuldade && (
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Nível de dificuldade</p><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getDificuldadeCls(viewModal.dificuldade)}`}>{viewModal.dificuldade}</span></div>
                )}
                {showAtividade && viewModal.tipoAtividade && (
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Tipo de atividade</p><p className="font-semibold text-slate-800 text-sm">{viewModal.tipoAtividade}</p></div>
                )}
                {showGuias && viewModal.guia_id && (
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Guia responsável</p><p className="font-semibold text-slate-800 text-sm">{guias.find(g => g.id === viewModal.guia_id)?.nome || '—'}</p></div>
                )}
              </div>

              <MiniSeatMap capacidade={viewModal.capacidade} reservas={viewModal.reservas} onPrintSeat={assento => downloadPDF(viewModal.id, 'embarque', assento)} />

              {showItinerario && viewModal.itinerario?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 text-sm mb-2 flex items-center gap-1.5"><Navigation className="w-4 h-4 text-slate-400" /> Itinerário</h3>
                  <div className="space-y-2">
                    {viewModal.itinerario.map((d, i) => (
                      <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: 'var(--brand-primary, #0147a0)' }}>
                        <p className="text-sm font-semibold text-slate-800">Dia {i + 1}{d.titulo ? ` — ${d.titulo}` : ''}</p>
                        {d.hospedagem && <p className="text-xs text-slate-500"><span className="font-medium">Hospedagem:</span> {d.hospedagem}</p>}
                        {d.refeicoes && <p className="text-xs text-slate-500"><span className="font-medium">Refeições:</span> {d.refeicoes}</p>}
                        {d.atividades && <p className="text-xs text-slate-600">{d.atividades}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showGaleria && viewModal.galeriaUrl && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-1.5"><Image className="w-4 h-4 text-slate-400" /> Galeria de fotos</h3>
                    <a href={viewModal.galeriaUrl} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1 text-blue-600 hover:underline"><ExternalLink className="w-3 h-3" /> Abrir no Drive</a>
                  </div>
                  <iframe title="Galeria do Drive" src={driveEmbedUrl(viewModal.galeriaUrl)} className="w-full rounded-lg border border-slate-200" style={{ height: 320 }} />
                </div>
              )}

              {showWaitlist && (
                <WaitlistSection viagemId={viewModal.id} canWrite={canWrite} />
              )}

              {viewModal.reservas?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 text-sm mb-2">Reservas ({viewModal.reservas.filter(r => r.status !== 'cancelada').length})</h3>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold">Pol.</th>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold">Passageiro</th>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold hidden sm:table-cell">Pagamento</th>
                          <th className="text-left px-3 py-2 text-slate-500 font-semibold">Status</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {[...viewModal.reservas].sort((a, b) => (a.assento || 0) - (b.assento || 0)).map(r => {
                          const { cls, label } = getReservaStatusStyle(r.status, r.forma_pagamento);
                          const wa = waLink(r.cliente?.telefone);
                          return (
                            <tr key={r.id} className={r.status === 'cancelada' ? 'opacity-40' : 'hover:bg-slate-50/70'}>
                              <td className="px-3 py-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded bg-slate-100 font-bold text-slate-700">{r.assento}</span></td>
                              <td className="px-3 py-2"><p className="font-medium text-slate-800">{r.cliente?.nome || '—'}</p>{r.cliente?.telefone && <p className="text-slate-400 text-[10px]">{r.cliente.telefone}</p>}</td>
                              <td className="px-3 py-2 hidden sm:table-cell"><span className="text-slate-600">{r.forma_pagamento || '—'}</span>{r.valor > 0 && <p className="text-slate-400 text-[10px]">R$ {Number(r.valor).toFixed(2)}</p>}</td>
                              <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded font-medium ${cls}`}>{label}</span></td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1">
                                  {wa && <a href={wa} target="_blank" rel="noreferrer" title="WhatsApp" className="p-1 rounded text-green-600 hover:bg-green-50"><MessageCircle className="w-3.5 h-3.5" /></a>}
                                  {r.status !== 'cancelada' && <button onClick={() => downloadPDF(viewModal.id, 'embarque', r.assento)} title="Imprimir Passagem" className="p-1 rounded hover:bg-blue-50" style={{ color: 'var(--brand-primary, #0147a0)' }}><Ticket className="w-3.5 h-3.5" /></button>}
                                  {r.status !== 'cancelada' && <button onClick={() => downloadRecibo(r.id, r.cliente?.nome)} title="Gerar Recibo" className="p-1 rounded text-amber-600 hover:bg-amber-50"><Receipt className="w-3.5 h-3.5" /></button>}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="border-t border-slate-100 pt-4">
                <h3 className="font-semibold text-slate-700 text-sm mb-3">Relatórios / PDF</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    { type: 'motorista', label: 'Lista p/ Motorista', style: { background: 'var(--brand-primary, #0147a0)' }, textCls: 'text-white', icon: <Printer className="w-4 h-4" /> },
                    ...(moduleEnabled(branding, 'bagagem') ? [
                      { type: 'bagagem', label: 'Lista de Bagagem', cls: 'border border-amber-300 text-amber-700 hover:bg-amber-50', icon: <Package className="w-4 h-4" /> },
                    ] : []),
                    ...(moduleEnabled(branding, 'encomendas') ? [
                      { type: 'encomendas', label: 'Lista de Encomendas', cls: 'border border-teal-300 text-teal-700 hover:bg-teal-50', icon: <Truck className="w-4 h-4" /> },
                    ] : []),
                    { type: 'embarque', label: 'Bilhetes de Embarque', cls: 'border border-slate-300 text-slate-700 hover:bg-slate-50', icon: <ClipboardList className="w-4 h-4" /> },
                    { type: 'relatorio', label: 'Relatório Completo', cls: 'border border-slate-300 text-slate-700 hover:bg-slate-50', icon: <FileText className="w-4 h-4" /> },
                  ].map(({ type, label, cls, style, textCls, icon }) => (
                    <button key={type} onClick={() => openExportModal(viewModal.id, type)} disabled={loadingPDF}
                      className={`flex items-center gap-2 font-medium px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-60 ${cls || ''} ${textCls || ''}`}
                      style={style}>
                      {icon}{label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportFormatModal
        open={exportModal.open}
        label={exportModal.label}
        loadingExcel={loadingExcel}
        onClose={closeExportModal}
        onPDF={() => downloadPDF(exportModal.viagemId, exportModal.type)}
        onExcel={() => downloadExcel(exportModal.viagemId, exportModal.type)}
      />

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2">Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Deseja excluir esta viagem? Reservas ativas vinculadas impedirão a exclusão.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
